<template>
  <Teleport :disabled="!isComponentActive" to="body">
    <div class="fixed top-0 left-0 w-full h-full z-[399] select-none" :class="[ isInit ? '' : 'pointer-events-none' ]" @click="isActive = false" v-if="isActive" />

    <ReactionList :is-active="isShowReactions" :reactions="message?.reactions ?? post?.reactions" @close="isShowReactions = false" />

    <div
      class="fixed top-0 left-0 z-[400] duration-300 sm:duration-100 transition-opacity text-sm select-none"
      :class="[
        isActive ? 'opacity-100' : 'opacity-0 pointer-events-none',
      ]"
      :style="style"
      @mouseleave="activeItemId = null"
      ref="$container"
    >
      <div class="sm:hidden fixed top-0 left-0 w-full h-full z-10 bg-charcoal-950/30 dark:bg-dark-600/80" :class="[ isInit ? '' : 'pointer-events-none' ]" @click="isActive = false" v-if="isActive" />

      <div class="sm:flex items-start" :class="isLeft ? 'sm:flex-row-reverse sm:-translate-x-full' : 'justify-end'" v-if="menu.length > 0">
        <div
          class="fixed bottom-0 left-0 z-20 sm:relative bg-royal-100 dark:bg-dark-920 dark:border-dark-500 space-y-0.5 rounded-t-2xl sm:rounded-md p-3 md:!pb-2 md:p-2 w-full min-h-[45vh] pb-10 sm:pb-2 sm:min-h-0 sm:w-60 sm:border border-royal-200/50 sm:shadow-md duration-300 sm:m-1"
          :style="{ paddingBottom: 'max(40px, (var(--inset-bottom) + 20px))'}"
          :class="[
            isActive ? 'translate-y-0' : 'translate-y-full sm:translate-y-0'
          ]"
          ref="$main"
        >
          <div class="flex items-center justify-between md:-mx-2 -mx-3 shrink-0 overflow-auto py-2 px-2.5 space-x-2 no-scrollbar" v-if="types.includes('react')">
            <ImageEmoji
              v-for="(value, id) in REACTIONS"
              :name="value.name"
              class="w-14 sm:w-10 shrink-0 cursor-pointer scale-90 hover:scale-100 transition"
              @click="() => onReaction(id)"
            />
          </div>
        
          <ContextItem
            v-for="(item, i) in menu"
            :item="item"
            :is-active="activeItemId === item.id"
            @mouseenter="() => item.children && item.children.length > 0 ? activeItemId = item.id : ''"
            @open="() => item.children && item.children.length > 0 ? activeItemId = item.id : ''"
            @close="isActive = false"
            @react-list="isShowReactions = true"
            @answer="options?.onAnswer"
            @edit="options?.onEdit"
            @retry="options?.onRetry"
            @cheers="options?.onCheers"
            :key="item.id || i"
          />
        </div>

        <div class="sm:hidden fixed top-0 w-full h-full z-30 bg-charcoal-950/30 dark:bg-dark-600/80" v-if="activeItemId" @click="() => { activeItemId = null; isCloseAll ? isActive = false : '' }" />

        <div
          class="fixed bottom-0 left-0 sm:relative w-full sm:w-60 sm:m-1 z-40"
          :class="[
            activeItemId && activeItemId === item.id ? '' : 'pointer-events-none'
          ]"
          v-for="item in menu.filter(i => i.children && i.children.length > 0)"
        >
          <div
            class="min-h-[50dvh] pb-10 sm:pb-1 sm:min-h-0 bg-royal-100 dark:bg-dark-920 dark:border-dark-500 -space-y-0.5 rounded-t-2xl sm:rounded-md p-3 md:!pb-2 md:p-2 sm:border border-royal-200 sm:shadow-md transition duration-300"
            :style="{ paddingBottom: 'max(40px, (var(--inset-bottom) + 20px))'}"
            :class="[
              activeItemId === item.id ? 'translate-y-0 sm:block' : 'translate-y-full sm:translate-y-0 sm:hidden'
            ]"
          >
            <div class="sm:hidden font-medium items-center justify-center !-ml-1 leading-none pt-1.5 pb-3 dark:text-dark-50" @click="activeItemId = ''" v-if="!isCloseAll">
              <IconBase name="cute/angle-right" class="-scale-x-100" /> {{ $t('actions.back' ) }}
            </div>
            
            <ContextItem
              v-for="(sub, j) in item.children"
              :item="sub"
              :parent="item"
              @close="isActive = false"
              :key="sub.id || j"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { REACTIONS, REACTIONS_FEATURED } from '@/static/reactions'
import UserStore from '@/store/user'
import ContextStore from '@/store/context'
import { ContextItem2, ContextActions } from '@/utils/useContext'
import { useContextWindow } from '@/utils/useContextWindow'
import ReactionsStore from '@/store/reactions'

const reactionsStore = ReactionsStore()

const $container = ref()
const $main = ref()

const { style, isActive, isLeft, setPosition } = useContextWindow($container)

const activeItemId: Ref<string | null> = ref(null)

const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const contextStore = ContextStore()
const { user, event, post, postParent, group, message, conversation, options } = storeToRefs(contextStore)

const menu: Ref<ContextItem2[]> = ref([])

const isInit = ref(false)
watch(isActive, (v) => {
  if (!isActive.value) activeItemId.value = null
  setTimeout(() => isInit.value = v, 200)
})

const types: Ref<(keyof typeof ContextActions)[]> = ref(['event', 'post', 'user-group', 'user'])

const isCloseAll = ref(false)

const onDisplay = (t: (keyof typeof ContextActions)[], id?: string) => {
  types.value = t ?? ['event', 'post', 'user-group', 'user']

  if (id) {
    activeItemId.value = id
    isCloseAll.value = true
  }

  const params = {
    post: post.value ?? undefined,
    postParent: postParent.value ?? undefined,
    event: event.value ?? undefined,
    group: group.value ?? undefined,
    user: user.value ?? undefined,
    message: message.value ?? undefined,
    conversation: conversation.value ?? undefined,
    currentUser: currentUser.value ?? undefined
  }

  menu.value = types.value.reduce((all: ContextItem2[], id) => {
    return [ ...all, ...ContextActions[id](params) ]
  }, [])

  setPosition()
}

const isShowReactions = ref(false)
const router = useRouter()
const route = useRoute()

const onReaction = async (reaction: keyof typeof REACTIONS) => {
  isActive.value = false

  if (post.value) {
    await reactionsStore.create({ value: reaction, type: 'post', id: post.value._id })
  } else if (message.value) {
    await reactionsStore.create({ value: reaction, type: 'message', id: message.value._id })
  }

  if (options.value?.onReact) options.value.onReact()
}

const isComponentActive = ref(false)

const onStart = () => {
  isComponentActive.value = true
}

const onLeave = () => {
  isComponentActive.value = false
}

onActivated(() => {
  onStart()
})

onMounted(() => {
  onStart()
})

onDeactivated(() => {
  onLeave()
})

onBeforeUnmount(() => {
  onLeave()
})

defineExpose({
  onDisplay,
  onShowReactions: () => isShowReactions.value = true
})
</script>